import type {
  AllSectionItem,
  GuideStep,
  SectionKey,
  UseCase,
  UserPreferences,
} from './types';
import { WELCOME_STEPS } from './steps/welcomeSteps';
import { CANVAS_BASICS_STEPS } from './steps/canvasSteps';
import { BLOCK_STEPS } from './steps/blockSteps';
import { CONNECTOR_STEPS } from './steps/connectorSteps';
import { GROUPS_LAYERS_STEPS } from './steps/groupsLayersSteps';
import { LAYER_NAV_STEPS } from './steps/layerNavSteps';
import { MULTI_SHEET_STEPS } from './steps/multiSheetSteps';
import { SAVE_LOAD_STEPS } from './steps/saveLoadSteps';
import { SHORTCUTS_STEPS } from './steps/shortcutsSteps';
import { ADVANCED_STEPS } from './steps/advancedSteps';
import { GENERIC_STEPS } from './steps/genericSteps';

/**
 * Maps the 7 WorkflowWizardDialog template IDs (from src/lib/workflowTemplates.ts)
 * to the 7 onboarding UseCase values. Keep both arrays in sync if templates change.
 */
export const TEMPLATE_TO_USECASE: Record<string, UseCase> = {
  'it-infra':  'it-infrastructure',
  'business':  'business-process',
  'org':       'organization-chart',
  'decision':  'decision-tree',
  'mindmap':   'mind-map',
  'project':   'project-planning',
  'advanced':  'sandbox',
};

export function mapTemplateToUseCase(templateId: string | null | undefined): UseCase {
  if (!templateId) return 'sandbox';
  return TEMPLATE_TO_USECASE[templateId] ?? 'sandbox';
}

export const GUIDE_STEPS: GuideStep[] = [
  ...WELCOME_STEPS,
  ...CANVAS_BASICS_STEPS,
  ...BLOCK_STEPS,
  ...CONNECTOR_STEPS,
  ...GROUPS_LAYERS_STEPS,
  ...LAYER_NAV_STEPS,
  ...MULTI_SHEET_STEPS,
  ...SAVE_LOAD_STEPS,
  ...SHORTCUTS_STEPS,
  ...ADVANCED_STEPS,
  ...GENERIC_STEPS,
];

export const ALL_SECTIONS: AllSectionItem[] = [
  {
    id: 'welcome', page: 'index',
    label: { en: 'Welcome', sv: 'Välkommen' },
    description: {
      en: 'A guided tour of the top bar, sidebar, canvas and properties panel.',
      sv: 'En rundtur genom toppraden, sidopanelen, canvasen och egenskapspanelen.',
    },
    estMinutes: 3, recommended: true,
  },
  {
    id: 'canvas-basics', page: 'index',
    label: { en: 'Canvas basics', sv: 'Canvas-grunder' },
    description: {
      en: 'Pan, zoom, the grid and how the minimap works.',
      sv: 'Panorera, zooma, rutnätet och hur minikartan fungerar.',
    },
    estMinutes: 2,
  },
  {
    id: 'blocks', page: 'index',
    label: { en: 'Blocks', sv: 'Block' },
    description: {
      en: 'Create blocks, configure name, description, color and actions.',
      sv: 'Skapa block, konfigurera namn, beskrivning, färg och åtgärder.',
    },
    estMinutes: 3, recommended: true,
  },
  {
    id: 'connectors', page: 'index',
    label: { en: 'Connectors', sv: 'Kopplingar' },
    description: {
      en: 'Draw connectors between block ports, add waypoints and labels.',
      sv: 'Dra kopplingar mellan portar, lägg till waypoints och etiketter.',
    },
    estMinutes: 2,
  },
  {
    id: 'groups-layers', page: 'index',
    label: { en: 'Groups & Layers', sv: 'Grupper & Lager' },
    description: {
      en: 'Group blocks visually or collapse them into navigable layers.',
      sv: 'Gruppera block visuellt eller fäll ihop dem till navigerbara lager.',
    },
    estMinutes: 2,
  },
  {
    id: 'multi-sheet', page: 'index',
    label: { en: 'Multi-sheet', sv: 'Flera blad' },
    description: {
      en: 'Split large workflows across multiple sheets and link them.',
      sv: 'Dela upp stora workflows på flera blad och koppla ihop dem.',
    },
    estMinutes: 2, minPlan: 'advanced',
  },
  {
    id: 'layer-nav', page: 'index',
    label: { en: 'Layer navigation', sv: 'Lagernavigation' },
    description: {
      en: 'Dive into layers and use breadcrumbs to navigate back.',
      sv: 'Dyk ner i lager och använd breadcrumbs för att gå tillbaka.',
    },
    estMinutes: 2,
  },
  {
    id: 'save-load', page: 'index',
    label: { en: 'Save, load & export', sv: 'Spara, ladda & exportera' },
    description: {
      en: 'Cloud auto-save, manual saves and PNG/SVG/JSON export.',
      sv: 'Auto-spara i molnet, manuell sparning och PNG/SVG/JSON-export.',
    },
    estMinutes: 2,
  },
  {
    id: 'shortcuts', page: 'index',
    label: { en: 'Keyboard shortcuts', sv: 'Kortkommandon' },
    description: {
      en: 'Undo/redo, save, delete, pan and zoom shortcuts.',
      sv: 'Ångra/gör om, spara, ta bort, panorera och zooma med tangenter.',
    },
    estMinutes: 2, interest: 'shortcuts',
  },
  {
    id: 'advanced', page: 'index',
    label: { en: 'Advanced', sv: 'Avancerat' },
    description: {
      en: 'AI assistant, 3D view and navigating very large workflows.',
      sv: 'AI-assistenten, 3D-vy och att navigera mycket stora workflows.',
    },
    estMinutes: 3,
  },
  {
    id: 'generic', page: 'index',
    label: { en: 'Quick tour', sv: 'Snabbrundtur' },
    description: {
      en: 'A six-step intro for visitors who are not signed in.',
      sv: 'En sex-stegs intro för besökare som inte är inloggade.',
    },
    estMinutes: 2,
  },
];

export const SECTION_ORDER: SectionKey[] = [
  'welcome', 'canvas-basics', 'blocks', 'connectors',
  'groups-layers', 'layer-nav', 'multi-sheet', 'save-load',
  'shortcuts', 'advanced',
];

const PLAN_RANK: Record<string, number> = {
  free: 0, advanced: 1, pro: 2, max: 3, enterprise: 4,
};

export interface FilterContext {
  preferences: UserPreferences | null;
  planTier?: string;
  isMobile?: boolean;
  isAuthenticated?: boolean;
}

export function filterStepsForUser(
  steps: GuideStep[],
  section: SectionKey,
  ctx: FilterContext,
): GuideStep[] {
  const prefs = ctx.preferences;
  const userPlanRank = PLAN_RANK[ctx.planTier ?? 'free'] ?? 0;

  return steps
    .filter(s => s.section === section)
    .filter(s => {
      if (s.requiresAuth && !ctx.isAuthenticated) return false;
      if (s.minPlan && PLAN_RANK[s.minPlan] > userPlanRank) return false;
      if (ctx.isMobile && s.mobileTarget === null) return false;
      if (!prefs) return true;
      if (s.useCases && s.useCases.length && !s.useCases.includes(prefs.useCase)) return false;
      if (s.roles && s.roles.length && !s.roles.includes(prefs.role)) return false;
      if (s.experience && s.experience.length && !s.experience.includes(prefs.experience)) return false;
      if (s.interests && s.interests.length) {
        const matches = s.interests.some(i => prefs.interests.includes(i));
        if (!matches) return false;
      }
      return true;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function getAvailableSections(ctx: FilterContext): AllSectionItem[] {
  const userPlanRank = PLAN_RANK[ctx.planTier ?? 'free'] ?? 0;
  return ALL_SECTIONS.filter(s => {
    if (s.id === 'generic') return !ctx.isAuthenticated;
    if (s.minPlan && PLAN_RANK[s.minPlan] > userPlanRank) return false;
    if (s.interest && ctx.preferences && !ctx.preferences.interests.includes(s.interest)) return false;
    return true;
  });
}
